import { BuilderContent } from "@builder.io/sdk";
import { useTranslation } from "next-i18next";
import React from "react";

import { parallel } from "@/utils/promises";
import serverSideTranslations from "@/utils/serverSideTranslations";

import { init } from "@/builder";
import { BuilderModel } from "@/builder/types";

import DefaultLayout from "@/layouts/default";

import { getBuilderMessages, getBuilderNavLinks } from "@/pages/api/builder/builder";
import { getFooterNavigation, getHeaderNavigation } from "@/pages/api/store-directory/navigation";
import { fetchStoreLandingPage } from "@/pages/api/store-directory/stores";

import { MobileRetailerPage } from "@/components/mobile-retailer-pages/mobile-retailer-page";
import { RetailerLandingPage } from "@/components/retailer-landing-pages/retailer-landing-page";
import { RetailerPage } from "@/components/retailer-pages/retailer-page";

import useViewport from "@/hooks/useViewport";

import { ChunkedNav, GenericNavItem } from "@/interfaces/navigation-interface";
import { StoreProps } from "@/interfaces/stores-interface";

import { Locale } from "@/types/locale";
import { GetServerSideProps } from "@/types/page";

type LocaleProps = {
    locale: Locale;
};

type NavProps = {
    headerNavigationItems: ChunkedNav;
    footerLocalNavItems: ChunkedNav;
    footerRegionalNavItems: Array<Array<GenericNavItem>>;
};

type CurrentRetailerPageProps = {
    store: StoreProps;
};

type OwnProps = {
    builderMessages: Array<BuilderContent>;
    builderNavLinks: Array<BuilderContent>;
};

type Props = OwnProps & NavProps & CurrentRetailerPageProps & LocaleProps;

type Params = {
    slug: string;
};

init(BuilderModel.MESSAGE);

export const getServerSideProps: GetServerSideProps<Props, Params> = async ({
    locale,
    resolvedUrl,
    params,
}) => {
    const store = await fetchStoreLandingPage(locale, params.slug);
    if (!store) {
        return {
            notFound: true,
        };
    }

    const parallelProps = await parallel({
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        builderMessages: () => getBuilderMessages(locale, { url: resolvedUrl }),
        builderNavLinks: () => getBuilderNavLinks(locale),
        _nextI18Next: () => serverSideTranslations(locale),
    });

    return {
        props: {
            locale,
            store,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            ...parallelProps,
        },
    };
};

const PageContent = ({
    store,
    locale,
    headerNavigationItems,
    footerLocalNavItems,
    footerRegionalNavItems,
    builderMessages,
    builderNavLinks,
}: Props): React.ReactNode => {
    const { isMobile } = useViewport();
    const { t } = useTranslation();

    return (
        <DefaultLayout
            currentLocale={locale}
            headerNavigationItems={headerNavigationItems}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderNavLinks={builderNavLinks}
            builderMessages={builderMessages}
            title={t("retailerPages.title", { merchantName: store.name })}
            description={t("retailerPages.description", {
                merchantName: store.name,
            })}
        >
            {locale === "en-US" ? (
                <RetailerLandingPage store={store} />
            ) : isMobile ? (
                <MobileRetailerPage store={store} locale={locale} />
            ) : (
                <RetailerPage store={store} locale={locale} />
            )}
        </DefaultLayout>
    );
};

export default PageContent;
